import { ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasSensorRoleFromActiveProfile } from '@redux/authent/authent.selectors';
import { usePostAutomatonCommandMutation } from '@services/c2/c2.api';
import { createSensorToast } from '@utils/sensors/sensors.utils';

import {
  AutomatonCabinetDefenceCommand,
  AutomatonCommandEnum,
  AutomatonReinitializeCommand,
  AutomatonSleepCommand,
} from '@/types/c2/c2.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';
import { AutomatonCabinet, AutomatonSensorConfiguration } from '@/types/sensor/sensors';

export interface UseCabinetCommandParameters {
  parentSensorConfiguration: AutomatonSensorConfiguration | null;
  cabinetConfiguration: AutomatonCabinet | null;
}

export interface UseCabinetCommandFunctions {
  canCommand: boolean;
  canRepulse: boolean;
  canDetect: boolean;
  sleep: (sleep: boolean) => void;
  repulse: (enableRepulsion: boolean) => void;
  reinitialize: () => void;
  isSleepCommandLoading: boolean;
  isRepulsionCommandLoading: boolean;
  isReinitializationCommandLoading: boolean;
}

export const useCabinetCommand: (params: UseCabinetCommandParameters) => UseCabinetCommandFunctions = ({
  parentSensorConfiguration,
  cabinetConfiguration,
}: UseCabinetCommandParameters) => {
  const canSendPerimeterCommands = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.COMMAND, sensorFamily: SensorFamilyEnum.AUTOMATON }),
  );
  const canSendRepulsionCommand = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, {
      action: ActionEnum.ACTIVATE_REPULSION,
      sensorFamily: SensorFamilyEnum.AUTOMATON,
    }),
  );
  const canSendDetectionCommand = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, {
      action: ActionEnum.DEACTIVATE_REPULSION,
      sensorFamily: SensorFamilyEnum.AUTOMATON,
    }),
  );

  const [postSleepCommand, { isLoading: isSleepLoading }] = usePostAutomatonCommandMutation();
  const [postRepulsionCommand, { isLoading: isRepulsionLoading }] = usePostAutomatonCommandMutation();
  const [postReinitCommand, { isLoading: isReinitLoading }] = usePostAutomatonCommandMutation();

  const handleCabinetPower = (sleep: boolean) => {
    if (parentSensorConfiguration !== null && cabinetConfiguration !== null) {
      const command: AutomatonSleepCommand = {
        type: AutomatonCommandEnum.AUTOMATON_COMMAND_SLEEP,
        sensorCode: parentSensorConfiguration.code,
        cabinetId: cabinetConfiguration.nodeId,
        sleep,
      };
      const toastParams = {
        commandValues: { state: sleep },
        command: 'sensors.commands.cabinet.sleepCommand',
        name: cabinetConfiguration.name,
      };
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.INFO,
      });
      postSleepCommand({
        site: parentSensorConfiguration.site,
        appCode: parentSensorConfiguration.appCode,
        command,
      })
        .unwrap()
        .then(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.SUCCESS,
          }),
        )
        .catch(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.ERROR,
          }),
        );
    }
  };

  const handleCabinetRepulsion = (enableRepulsion: boolean) => {
    if (parentSensorConfiguration !== null && cabinetConfiguration !== null) {
      const command: AutomatonCabinetDefenceCommand = {
        type: AutomatonCommandEnum.AUTOMATON_CABINET_COMMAND_DEFENCE,
        sensorCode: parentSensorConfiguration.code,
        cabinetId: cabinetConfiguration.nodeId,
        defence: enableRepulsion,
      };
      const toastParams = {
        commandValues: { state: enableRepulsion },
        command: 'sensors.commands.cabinet.defenceCommand',
        name: cabinetConfiguration.name,
      };
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.INFO,
      });
      postRepulsionCommand({
        site: parentSensorConfiguration.site,
        appCode: parentSensorConfiguration.appCode,
        command,
      })
        .unwrap()
        .then(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.SUCCESS,
          }),
        )
        .catch(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.ERROR,
          }),
        );
    }
  };

  const handleReinitCabinet = () => {
    if (parentSensorConfiguration !== null && cabinetConfiguration !== null) {
      const command: AutomatonReinitializeCommand = {
        type: AutomatonCommandEnum.AUTOMATON_COMMAND_REINITIALIZE,
        sensorCode: parentSensorConfiguration.code,
        cabinetId: cabinetConfiguration.nodeId,
        reinitialize: true,
      };
      const toastParams = {
        command: 'sensors.commands.cabinet.alarmCommand',
        name: cabinetConfiguration.name,
      };
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.INFO,
      });
      postReinitCommand({
        site: parentSensorConfiguration.site,
        appCode: parentSensorConfiguration.appCode,
        command,
      })
        .unwrap()
        .then(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.SUCCESS,
          }),
        )
        .catch(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.ERROR,
          }),
        );
    }
  };

  return {
    canCommand: canSendPerimeterCommands,
    canDetect: canSendDetectionCommand,
    canRepulse: canSendRepulsionCommand,
    isRepulsionCommandLoading: isRepulsionLoading,
    isSleepCommandLoading: isSleepLoading,
    isReinitializationCommandLoading: isReinitLoading,
    repulse: handleCabinetRepulsion,
    sleep: handleCabinetPower,
    reinitialize: handleReinitCabinet,
  };
};
